import React, { useState, useEffect, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { connect } from "react-redux";
import queryString from "query-string";
import { makeStyles } from "tss-react/mui";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import CoffeeIcon from "@mui/icons-material/LocalCafe";
import EmailIcon from "@mui/icons-material/Email";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ErrorIcon from "@mui/icons-material/Error";
import RefreshIcon from "@mui/icons-material/Refresh";
import axios from "axios";
import { loadBatchContent } from "../../redux/actions/userActions";
import { SET_BATCH_CONTENT_LOADING } from "../../redux/types";

const useStyles = makeStyles()((theme) => ({
  container: {
    height: "100vh",
    width: "100vw",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    padding: theme.spacing(2),
    margin: 0,
    position: "fixed",
    top: 0,
    left: 0,
    zIndex: 1000,
    overflow: "auto",
    boxSizing: "border-box",
  },
  card: {
    maxWidth: 600,
    width: "100%",
    textAlign: "center",
    borderRadius: 16,
    boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
    margin: theme.spacing(2, 0),
    flexShrink: 0,
  },
  cardContent: {
    padding: theme.spacing(4),
  },
  title: {
    fontWeight: 600,
    marginBottom: theme.spacing(2),
    color: theme.palette.primary.main,
  },
  subtitle: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(3),
    fontSize: "1.1rem",
  },
  statsContainer: {
    display: "flex",
    justifyContent: "space-around",
    margin: theme.spacing(2, 0),
    flexWrap: "wrap",
    gap: theme.spacing(1),
  },
  statChip: {
    padding: theme.spacing(1, 2),
    fontSize: "0.9rem",
  },
  encouragementBox: {
    backgroundColor: theme.palette.background.default,
    borderRadius: 12,
    padding: theme.spacing(2),
    margin: theme.spacing(3, 0),
    border: `1px solid ${theme.palette.divider}`,
  },
  encouragementText: {
    color: theme.palette.text.primary,
    fontWeight: 500,
    marginBottom: theme.spacing(1),
  },
  encouragementSubtext: {
    color: theme.palette.text.secondary,
    fontSize: "0.9rem",
  },
  actionButton: {
    marginTop: theme.spacing(2),
    borderRadius: 8,
    textTransform: "none",
    fontWeight: 600,
  },
  iconContainer: {
    fontSize: 48,
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(2),
  },
  spinnerContainer: {
    margin: theme.spacing(3, 0),
  },
  progressContainer: {
    margin: theme.spacing(3, 0),
  },
  progressBar: {
    height: 8,
    borderRadius: 4,
    marginBottom: theme.spacing(2),
  },
  progressText: {
    color: theme.palette.text.secondary,
    fontSize: "0.9rem",
    textAlign: "center",
  },
  proceedButton: {
    marginTop: theme.spacing(2),
    borderRadius: 8,
    textTransform: "none",
    fontWeight: 600,
    marginLeft: theme.spacing(1),
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
}));

function ContentGenerationLoading(props) {
  const { classes } = useStyles();
  const history = useHistory();
  const location = useLocation();
  const parsedQS = queryString.parse(location.search);

  const batchId = parsedQS.batchId;
  const totalPosts = parseInt(parsedQS.totalPosts) || 0;

  // State for progress tracking
  const [batchStatus, setBatchStatus] = useState("processing");
  const [processedPosts, setProcessedPosts] = useState(0);
  const [currentStatus, setCurrentStatus] = useState("Starting generation...");
  const [isCompleted, setIsCompleted] = useState(false);
  const [isFailed, setIsFailed] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [batchInfo, setBatchInfo] = useState(null);
  const [isRecurring, setIsRecurring] = useState(false);
  const [scanningProgress, setScanningProgress] = useState(null);
  const [hasBatchContentLoaded, setHasBatchContentLoaded] = useState(false);

  // Use ref to track completion status for polling cleanup
  const isCompletedRef = useRef(false);
  const pollIntervalRef = useRef(null);

  // Calculate progress percentage
  // Note: processedPosts represents processing milestones, not actual completed posts
  let progressPercentage =
    totalPosts > 0 ? Math.round((processedPosts / totalPosts) * 100) : 0;

  // If we're in scanning phase, show scanning progress instead
  if (batchInfo?.processingPhase === "scraping" && batchInfo?.scrapingStatus) {
    const { completedJobs, totalJobs } = batchInfo.scrapingStatus;
    if (totalJobs > 0) {
      // Scanning represents the first 40% of the process
      progressPercentage = Math.round((completedJobs / totalJobs) * 40);
    }
  }

  // Set up API polling for batch progress
  useEffect(() => {
    if (!batchId) return;

    const pollBatchStatus = async () => {
      // Check if already completed to avoid unnecessary API calls
      if (isCompletedRef.current) {
        console.log("Batch already completed, stopping polling");
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        return;
      }

      try {
        const response = await axios.get(
          `/api/generatedContent/batch/${batchId}`
        );
        const data = response.data;

        if (data.status) {
          setBatchStatus(data.status);
          setBatchInfo(data);
          setIsRecurring(data.isRecurring || false);
          setScanningProgress(data.scrapingProgress || null);

          // When completed, processedPosts should equal totalPosts
          if (data.status === "completed") {
            const completedPosts = data.totalPosts || totalPosts;
            setProcessedPosts(completedPosts);
            setCurrentStatus("Content generation completed!");
            setIsCompleted(true);
            isCompletedRef.current = true; // Update ref immediately

            // Clear the polling interval
            if (pollIntervalRef.current) {
              console.log("Content generation completed, stopping polling");
              clearInterval(pollIntervalRef.current);
              pollIntervalRef.current = null;
            }

            // Load batch content immediately when generation completes (only once)
            if (!hasBatchContentLoaded) {
              console.log(
                `🎯 Loading batch content immediately for batchId: ${batchId}`
              );
              setHasBatchContentLoaded(true);

              // Set loading state before loading batch content
              props.dispatch({
                type: SET_BATCH_CONTENT_LOADING,
                payload: true,
              });

              // Use the action creator directly from props
              props
                .loadBatchContent(batchId)
                .then(() => {
                  console.log(
                    `✅ Batch content loaded successfully for batchId: ${batchId}`
                  );
                  setCurrentStatus("Content loaded and ready!");
                })
                .catch((err) => {
                  console.error(
                    `❌ Failed to load batch content for batchId: ${batchId}`,
                    err
                  );
                  setCurrentStatus(
                    "Content generation completed! (Loading failed)"
                  );
                  setHasBatchContentLoaded(false); // Allow retry
                });
            }
          } else if (data.status === "failed") {
            // Handle failed status
            setProcessedPosts(0);
            setCurrentStatus("Content generation failed");
            setErrorMessage(
              data.error ||
                "An unknown error occurred during content generation"
            );
            setIsCompleted(false);
            setIsFailed(true);
            isCompletedRef.current = true; // Stop polling

            // Clear the polling interval
            if (pollIntervalRef.current) {
              console.log("Content generation failed, stopping polling");
              clearInterval(pollIntervalRef.current);
              pollIntervalRef.current = null;
            }
          } else {
            setProcessedPosts(data.processedPosts || 0);

            // Update status based on processing phase
            let statusText = data.status || "Processing...";
            if (data.processingPhase === "scraping" && data.scrapingStatus) {
              const { completedJobs, totalJobs, failedJobs } =
                data.scrapingStatus;
              if (data.scrapingStatus.isScrapingComplete) {
                statusText = `Scanning complete! Starting content generation...`;
              } else {
                statusText = `Scanning content: ${completedJobs}/${totalJobs} posts scanned`;
                // Don't show failed jobs for now
                // if (failedJobs > 0) {
                //   statusText += ` (${failedJobs} failed)`;
                // }
              }
            } else if (data.processingPhase === "content_generation") {
              statusText = "Generating content and applying designs...";
            }

            setCurrentStatus(statusText);
            setIsCompleted(false);
            isCompletedRef.current = false;
          }
        }
      } catch (error) {
        console.error("Error fetching batch progress:", error);
      }
    };

    // Poll immediately
    pollBatchStatus();

    // Set up polling interval (every 3 seconds)
    pollIntervalRef.current = setInterval(pollBatchStatus, 3000);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [batchId, totalPosts]);

  const handleContinueInBackground = () => {
    // Navigate back to connected websites or dashboard
    history.push("/automate");
  };

  const handleProceedToContent = () => {
    // Navigate to Generate page with batch ID
    history.push(`/generate?batchId=${batchId}`);
  };

  const handleRetryGeneration = () => {
    // Navigate back to connected websites to retry
    history.push("/automate");
  };

  return (
    <div className={classes.container}>
      <Card className={classes.card}>
        <CardContent className={classes.cardContent}>
          <div className={classes.iconContainer}>
            {isFailed ? (
              <ErrorIcon fontSize="inherit" style={{ color: "#f44336" }} />
            ) : isCompleted ? (
              <CheckCircleIcon
                fontSize="inherit"
                style={{ color: "#4caf50" }}
              />
            ) : (
              <HourglassEmptyIcon fontSize="inherit" />
            )}
          </div>

          <Typography variant="h4" className={classes.title}>
            {isFailed
              ? "Content Generation Failed"
              : isCompleted
              ? "Content Generation Complete!"
              : "Generating Your Content"}
          </Typography>

          <Typography variant="body1" className={classes.subtitle}>
            {isFailed
              ? "We encountered an issue while generating your content. Please try again or contact support if the problem persists."
              : isCompleted
              ? `Successfully created ${totalPosts} posts for your next 4 weeks of content!`
              : `We're processing and creating ${totalPosts} posts for your next 4 weeks of content.
                 We will email you when all your posts are ready.`}
          </Typography>

          {/* Error Message Display */}
          {isFailed && errorMessage && (
            <Box
              sx={{
                backgroundColor: "#ffebee",
                padding: 2,
                borderLeft: "4px solid #f44336",
                margin: "16px 0",
                borderRadius: 1,
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: "#d32f2f", marginBottom: 1 }}
              >
                ❌ Error Details
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {errorMessage}
              </Typography>
            </Box>
          )}

          {/* Scanning Progress Details */}
          {batchInfo?.processingPhase === "scraping" &&
            batchInfo?.scrapingStatus &&
            !isFailed && (
              <Box
                sx={{
                  backgroundColor: "#e3f2fd",
                  padding: 2,
                  borderLeft: "4px solid #2196f3",
                  margin: "16px 0",
                  borderRadius: 1,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: "#1976d2", marginBottom: 1 }}
                >
                  🔍 Scanning Progress
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ marginBottom: 1 }}
                >
                  Extracting content from your website posts...
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <LinearProgress
                    variant="determinate"
                    value={
                      batchInfo.scrapingStatus.totalJobs > 0
                        ? (batchInfo.scrapingStatus.completedJobs /
                            batchInfo.scrapingStatus.totalJobs) *
                          100
                        : 0
                    }
                    sx={{ flexGrow: 1, height: 6, borderRadius: 3 }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ minWidth: 80, textAlign: "right" }}
                  >
                    {batchInfo.scrapingStatus.completedJobs}/
                    {batchInfo.scrapingStatus.totalJobs} posts
                  </Typography>
                </Box>
                {/* {batchInfo.scrapingStatus.failedJobs > 0 && (
                  <Typography
                    variant="body2"
                    color="warning.main"
                    sx={{ marginTop: 1, fontSize: "0.875rem" }}
                  >
                    ⚠️ {batchInfo.scrapingStatus.failedJobs} posts failed to
                    scan (will use cached data if available)
                  </Typography>
                )} */}
              </Box>
            )}

          {/* Scheduling Information for Recurring Content */}
          {isRecurring && !isFailed && (
            <Box
              sx={{
                backgroundColor: "#f8f9fa",
                padding: 2,
                borderLeft: "4px solid #7B2CBF",
                margin: "16px 0",
                borderRadius: 1,
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: "#7B2CBF", marginBottom: 1 }}
              >
                📅 Scheduling Information
              </Typography>
              <Typography variant="body2" color="textSecondary">
                This is recurring content that will be scheduled to start{" "}
                <strong>one week from generation</strong>{" "}
                {batchInfo?.createdAt && (
                  <>
                    (
                    {new Date(
                      new Date(batchInfo.createdAt).getTime() +
                        7 * 24 * 60 * 60 * 1000
                    ).toLocaleDateString("en-US", {
                      weekday: "long",
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                    )
                  </>
                )}
                . This gives you time to review and approve the content before
                it goes live!
              </Typography>
            </Box>
          )}

          {/* Progress Bar */}
          {!isFailed && (
            <>
              {!isCompleted && (
                <Box className={classes.spinnerContainer}>
                  <CircularProgress size={60} thickness={4} />
                </Box>
              )}
              <Box className={classes.progressContainer}>
                <LinearProgress
                  variant="determinate"
                  value={progressPercentage}
                  className={classes.progressBar}
                  color={isCompleted ? "success" : "primary"}
                />
                <Typography className={classes.progressText}>
                  {isCompleted
                    ? `${totalPosts} posts completed (100%)`
                    : batchInfo?.processingPhase === "scraping" &&
                      batchInfo?.scrapingStatus
                    ? `Processing progress: ${progressPercentage}%`
                    : `Processing progress: ${progressPercentage}%`}
                </Typography>
                <Typography
                  className={classes.progressText}
                  style={{ marginTop: 4 }}
                >
                  {currentStatus}
                </Typography>
                {!isCompleted && (
                  <Typography
                    className={classes.progressText}
                    style={{
                      marginTop: 8,
                      fontSize: "0.8rem",
                      fontStyle: "italic",
                    }}
                  >
                    {progressPercentage < 30 &&
                      "Analyzing your website content..."}
                    {progressPercentage >= 30 &&
                      progressPercentage < 50 &&
                      "Gathering relevant posts..."}
                    {progressPercentage >= 50 &&
                      progressPercentage < 70 &&
                      "Creating posting schedules..."}
                    {progressPercentage >= 70 &&
                      progressPercentage < 90 &&
                      "Applying designs and generating content..."}
                    {progressPercentage >= 90 && "Finalizing your posts..."}
                  </Typography>
                )}
              </Box>
            </>
          )}

          {/* Scanning Progress (when available) */}
          {scanningProgress && (
            <Box className={classes.progressContainer} sx={{ mt: 1 }}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                📄 Content Scanning
              </Typography>
              <LinearProgress
                variant="determinate"
                value={Math.round(
                  (scanningProgress.scrapedPosts /
                    scanningProgress.totalPostsToScrape) *
                    100
                )}
                className={classes.progressBar}
                color={scanningProgress.isComplete ? "success" : "secondary"}
                sx={{ height: 6 }}
              />
              <Typography className={classes.progressText}>
                {scanningProgress.scrapedPosts} of{" "}
                {scanningProgress.totalPostsToScrape} posts scanned (
                {Math.round(
                  (scanningProgress.scrapedPosts /
                    scanningProgress.totalPostsToScrape) *
                    100
                )}
                %) {scanningProgress.isComplete && "✓ Complete"}
              </Typography>
            </Box>
          )}

          <div className={classes.statsContainer}>
            <Chip
              icon={<CheckCircleIcon />}
              label={`${totalPosts} Posts`}
              className={classes.statChip}
              color="primary"
              variant="outlined"
            />
            {scanningProgress && (
              <Chip
                icon={<HourglassEmptyIcon />}
                label={`${scanningProgress.scrapedPosts}/${scanningProgress.totalPostsToScrape} Scanned`}
                className={classes.statChip}
                color={scanningProgress.isComplete ? "success" : "info"}
                variant="outlined"
              />
            )}
            <Chip
              icon={<EmailIcon />}
              label="Email When Ready"
              className={classes.statChip}
              color="secondary"
              variant="outlined"
            />
          </div>

          {!isFailed && (
            <Box className={classes.encouragementBox}>
              <Typography className={classes.encouragementText}>
                <CoffeeIcon
                  style={{ verticalAlign: "middle", marginRight: 8 }}
                />
                This might take a while, take a break!
              </Typography>
              <Typography className={classes.encouragementSubtext}>
                We'll email you when all your posts are ready. You can close
                this page and the generation will continue in the background.
              </Typography>
            </Box>
          )}

          <Box className={classes.buttonContainer}>
            {isFailed ? (
              <>
                <Button
                  variant="outlined"
                  onClick={handleContinueInBackground}
                  className={classes.actionButton}
                >
                  Back to Dashboard
                </Button>
                <Button
                  variant="contained"
                  onClick={handleRetryGeneration}
                  className={classes.proceedButton}
                  startIcon={<RefreshIcon />}
                  color="primary"
                >
                  Try Again
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outlined"
                  onClick={handleContinueInBackground}
                  className={classes.actionButton}
                >
                  Continue in Background
                </Button>
                <Button
                  variant="contained"
                  onClick={handleProceedToContent}
                  className={classes.proceedButton}
                  disabled={!isCompleted || props.isBatchContentLoading}
                  startIcon={
                    props.isBatchContentLoading ? (
                      <CircularProgress size={16} />
                    ) : (
                      <PlayArrowIcon />
                    )
                  }
                >
                  {props.isBatchContentLoading
                    ? "Loading Content..."
                    : "Proceed to Content"}
                </Button>
              </>
            )}
          </Box>

          {batchId && (
            <Typography
              variant="caption"
              color="textSecondary"
              style={{ marginTop: 16, display: "block" }}
            >
              Batch ID: {batchId}
            </Typography>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

const mapStateToProps = (state) => ({
  posts: state.design.posts,
  isBatchContentLoading: state.design.isBatchContentLoading,
});

const mapDispatchToProps = (dispatch) => ({
  loadBatchContent: (batchId) => dispatch(loadBatchContent(batchId)),
  dispatch,
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ContentGenerationLoading);
